import { useCallback, useEffect, useRef, useState } from 'react';
import styles from '../../MetaReportViewer.module.css';

/* ─── Step definitions ────────────────────────────────────────────────── */

const FLOW_STEPS = [
  {
    id: 'prepare',
    label: 'Preparing request',
    detail: 'Collecting metrics and building payload',
  },
  {
    id: 'send',
    label: 'Sending to engine',
    detail: 'Connecting to the analysis service',
  },
  {
    id: 'generate',
    label: 'Generating AI analysis',
    detail: 'Zeqi is processing your data, this takes a moment',
  },
  {
    id: 'save',
    label: 'Saving to database',
    detail: 'Persisting the assessment so it loads instantly next time',
  },
  {
    id: 'complete',
    label: 'Analysis complete',
    detail: 'Your results are ready below',
  },
];

/* ─── Hook ────────────────────────────────────────────────────────────── */

export const useAnalysisFlow = () => {
  const [steps, setSteps] = useState(null);
  const timersRef = useRef([]);

  const clearTimers = () => {
    timersRef.current.forEach(clearTimeout);
    timersRef.current = [];
  };

  const advance = useCallback((toIndex) => {
    setSteps((prev) => {
      if (!prev) return prev;
      return prev.map((step, i) => {
        if (i < toIndex) return { ...step, status: 'done', endedAt: step.endedAt ?? Date.now() };
        if (i === toIndex) return { ...step, status: 'active', startedAt: Date.now() };
        return step;
      });
    });
  }, []);

  const start = useCallback(() => {
    clearTimers();
    setSteps(
      FLOW_STEPS.map((s, i) => ({
        ...s,
        status: i === 0 ? 'active' : 'pending',
        startedAt: i === 0 ? Date.now() : null,
        endedAt: null,
        errorMessage: null,
      })),
    );
    // Auto-advance through prepare → send → generating
    timersRef.current.push(setTimeout(() => advance(1), 700));
    timersRef.current.push(setTimeout(() => advance(2), 1900));
    // Step 2 (generate) holds until resolve() or fail()
  }, [advance]);

  const resolve = useCallback(() => {
    clearTimers();
    // Advance to "saving"
    setSteps((prev) => {
      if (!prev) return prev;
      return prev.map((step, i) => {
        if (i < 3) return { ...step, status: 'done', endedAt: step.endedAt ?? Date.now() };
        if (i === 3) return { ...step, status: 'active', startedAt: Date.now() };
        return step;
      });
    });
    // Then advance to "complete"
    timersRef.current.push(
      setTimeout(() => {
        setSteps((prev) =>
          prev?.map((step) => ({ ...step, status: 'done', endedAt: step.endedAt ?? Date.now() })) ?? null,
        );
      }, 700),
    );
  }, []);

  const fail = useCallback((message) => {
    clearTimers();
    setSteps((prev) => {
      if (!prev) return prev;
      const activeIndex = prev.findIndex((s) => s.status === 'active');
      const idx = activeIndex === -1 ? prev.length - 1 : activeIndex;
      return prev.map((step, i) => {
        if (i < idx) return { ...step, status: 'done', endedAt: step.endedAt ?? Date.now() };
        if (i === idx) return { ...step, status: 'error', errorMessage: message, endedAt: Date.now() };
        return step;
      });
    });
  }, []);

  const reset = useCallback(() => {
    clearTimers();
    setSteps(null);
  }, []);

  useEffect(() => () => clearTimers(), []);

  return { steps, start, resolve, fail, reset };
};

/* ─── Elapsed timer ───────────────────────────────────────────────────── */

const ElapsedTimer = ({ startedAt }) => {
  const [elapsed, setElapsed] = useState(0);

  useEffect(() => {
    if (!startedAt) return undefined;
    const id = setInterval(() => {
      setElapsed(Math.floor((Date.now() - startedAt) / 1000));
    }, 1000);
    return () => clearInterval(id);
  }, [startedAt]);

  if (!startedAt) return null;
  return <span className={styles.flowStepTimer}>{elapsed}s</span>;
};

/* ─── Step icon ───────────────────────────────────────────────────────── */

const StepIcon = ({ status }) => {
  if (status === 'done') {
    return (
      <span className={`${styles.flowStepIcon} ${styles.flowStepIconDone}`}>
        <svg width="11" height="11" viewBox="0 0 12 12" fill="none">
          <path d="M2 6l3 3 5-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </span>
    );
  }
  if (status === 'error') {
    return (
      <span className={`${styles.flowStepIcon} ${styles.flowStepIconError}`}>
        <svg width="11" height="11" viewBox="0 0 12 12" fill="none">
          <path d="M3 3l6 6M9 3l-6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
        </svg>
      </span>
    );
  }
  if (status === 'active') {
    return (
      <span className={`${styles.flowStepIcon} ${styles.flowStepIconActive}`}>
        <span className={styles.flowSpinner} />
      </span>
    );
  }
  return <span className={`${styles.flowStepIcon} ${styles.flowStepIconPending}`} />;
};

/* ─── Component ───────────────────────────────────────────────────────── */

export const AnalysisFlowLog = ({ steps, label = 'Analysis Progress' }) => {
  if (!steps) return null;

  return (
    <div className={styles.flowLogCard}>
      <div className={styles.flowLogHeader}>
        <span className={styles.flowLogTitle}>{label}</span>
        <span className={styles.flowLogPulse} />
      </div>
      <div className={styles.flowLogSteps}>
        {steps.map((step, i) => (
          <div
            key={step.id}
            className={`${styles.flowStep} ${styles[`flowStep_${step.status}`]}`}
          >
            {/* Connecting line */}
            {i < steps.length - 1 && (
              <span
                className={`${styles.flowStepLine} ${
                  step.status === 'done' ? styles.flowStepLineDone : ''
                }`}
              />
            )}

            <StepIcon status={step.status} />

            <div className={styles.flowStepBody}>
              <div className={styles.flowStepRow}>
                <span className={styles.flowStepLabel}>{step.label}</span>
                {step.status === 'active' && <ElapsedTimer startedAt={step.startedAt} />}
                {step.status === 'done' && step.startedAt && step.endedAt && (
                  <span className={styles.flowStepDuration}>
                    {((step.endedAt - step.startedAt) / 1000).toFixed(1)}s
                  </span>
                )}
              </div>
              {(step.status === 'active' || step.status === 'error') && (
                <p className={styles.flowStepDetail}>
                  {step.status === 'error' ? (step.errorMessage || 'Something went wrong') : step.detail}
                </p>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
